import React, { useContext, useEffect, useState } from 'react';
import { UserProfileContext } from '../context/userProfileContext';
import { camelCaseToText } from '../utils/camelCaseToText';
import { formatDate } from '../utils/formatDate';
import { Polywizz } from '../interfaces/Insurance';
import { getPolywizzStatusTrackers } from '../utils/api/insurance';

interface StatusHistoryProps {
    fetchStatusTrackers: (params: any) => Promise<{ statusTrackers: any[] }>;
    moduleId: string | undefined;
    moduleName: string;
    moduleIdParamName?: string; // e.g., 'pensionId', 'taxReturnId', etc.
    polywizzClient?: Polywizz | null;
    polywizzSpouse?: Polywizz | null;
}

interface CombinedTracker {
    _id: string;
    newStatus: string;
    createdAt: string;
    changer?: string;
    type: 'insurance' | 'polywizz';
    familyMemberType?: string;
}

const StatusHistory: React.FC<StatusHistoryProps> = ({
    fetchStatusTrackers,
    moduleId,
    moduleName,
    moduleIdParamName,
    polywizzClient,
    polywizzSpouse,
}) => {
    const { getUserById } = useContext(UserProfileContext);
    const [statusTrackers, setStatusTrackers] = useState<any[]>([]);
    const [loadingTrackers, setLoadingTrackers] = useState(false);
    const [includePolywizz, setIncludePolywizz] = useState(false);
    const [combinedTrackers, setCombinedTrackers] = useState<CombinedTracker[]>([]);

    useEffect(() => {
        const fetchTrackers = async () => {
            if (!moduleId) return;

            setLoadingTrackers(true);
            try {
                // Use provided param name or default to moduleName + 'Id'
                const paramName = moduleIdParamName || `${moduleName}Id`;
                const response = await fetchStatusTrackers({ [paramName]: moduleId });
                setStatusTrackers(response?.statusTrackers || []);
            } catch (error) {
                console.error(`Error fetching ${moduleName} status trackers:`, error);
                setStatusTrackers([]);
            } finally {
                setLoadingTrackers(false);
            }
        };

        fetchTrackers();
    }, [moduleId, fetchStatusTrackers, moduleName, moduleIdParamName]);

    useEffect(() => {
        const fetchAndCombineTrackers = async () => {
            if (!includePolywizz) {
                setCombinedTrackers([]);
                return;
            }

            setLoadingTrackers(true);
            try {
                const allTrackers: CombinedTracker[] = statusTrackers.map(t => ({
                    ...t,
                    type: 'insurance' as const,
                }));

                // Fetch client polywizz trackers
                if (polywizzClient?._id) {
                    const clientResponse = await getPolywizzStatusTrackers({ polywizzId: polywizzClient._id });
                    const clientTrackers = (clientResponse?.statusTrackers || []).map((t: any) => ({
                        ...t,
                        type: 'polywizz' as const,
                        familyMemberType: 'client',
                    }));
                    allTrackers.push(...clientTrackers);
                }

                // Fetch spouse polywizz trackers
                if (polywizzSpouse?._id) {
                    const spouseResponse = await getPolywizzStatusTrackers({ polywizzId: polywizzSpouse._id });
                    const spouseTrackers = (spouseResponse?.statusTrackers || []).map((t: any) => ({
                        ...t,
                        type: 'polywizz' as const,
                        familyMemberType: 'spouse',
                    }));
                    allTrackers.push(...spouseTrackers);
                }

                // Sort by createdAt ascending
                allTrackers.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
                setCombinedTrackers(allTrackers);
            } catch (error) {
                console.error('Error fetching polywizz status trackers:', error);
            } finally {
                setLoadingTrackers(false);
            }
        };

        fetchAndCombineTrackers();
    }, [includePolywizz, statusTrackers, polywizzClient, polywizzSpouse]);

    if (!moduleId) return null;

    const hasPolywizz = polywizzClient || polywizzSpouse;
    const displayTrackers = includePolywizz && combinedTrackers.length > 0 ? combinedTrackers : statusTrackers;

    return (
        <div>
            <h4 style={{ textAlign: 'left' }}>Status History</h4>
            {hasPolywizz && (
                <div style={{ marginBottom: '10px' }}>
                    <label style={{ cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={includePolywizz}
                            onChange={e => setIncludePolywizz(e.target.checked)}
                            style={{ marginRight: '8px' }}
                        />
                        Include Polywizz Status History
                    </label>
                </div>
            )}
            {loadingTrackers ? (
                <div>Loading status history...</div>
            ) : displayTrackers.length > 0 ? (
                <div style={{ maxWidth: '700px' }}>
                    <table className={`table table-bordered ${includePolywizz ? '' : 'table-striped'}`}>
                        <thead>
                            <tr>
                                {includePolywizz && <th>Type</th>}
                                <th>Status</th>
                                <th>Time of Change</th>
                                <th>Changer</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayTrackers.map((tracker: any, index: number) => {
                                const getRowStyle = () => {
                                    if (!includePolywizz) return {};
                                    if (tracker.type === 'polywizz') {
                                        return {
                                            backgroundColor: tracker.familyMemberType === 'client' ? '#bbdefb' : '#ffcdd2',
                                        };
                                    }
                                    return { backgroundColor: '#ffffff' };
                                };
                                return (
                                <tr
                                    key={tracker._id || index}
                                    style={getRowStyle()}
                                >
                                    {includePolywizz && (
                                        <td>
                                            {tracker.type === 'polywizz' ? (
                                                <span
                                                    style={{
                                                        padding: '2px 6px',
                                                        borderRadius: '4px',
                                                        fontSize: '11px',
                                                        backgroundColor: tracker.familyMemberType === 'client' ? '#1976d2' : '#c2185b',
                                                        color: 'white',
                                                    }}
                                                >
                                                    Polywizz ({tracker.familyMemberType})
                                                </span>
                                            ) : (
                                                <span
                                                    style={{
                                                        padding: '2px 6px',
                                                        borderRadius: '4px',
                                                        fontSize: '11px',
                                                        backgroundColor: '#388e3c',
                                                        color: 'white',
                                                    }}
                                                >
                                                    Insurance
                                                </span>
                                            )}
                                        </td>
                                    )}
                                    <td>{camelCaseToText(tracker.newStatus || '')}</td>
                                    <td>{formatDate(tracker.createdAt)}</td>
                                    <td>{tracker.changer ? getUserById(tracker.changer)?.fullName || 'auto' : 'auto'}</td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div>No status history available.</div>
            )}
        </div>
    );
};

export default StatusHistory;
